import { useQuery, useMutation } from "@tanstack/react-query";
import { trackEvent } from "@/hooks/useTracking";
import { formatTimeAgo } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Star,
  MessageSquare,
  Flag,
  Send,
  TrendingUp,
  AlertTriangle,
  Sparkles,
  QrCode,
  Link2,
  Copy,
  Check,
  Wand2,
  RotateCcw,
  ThumbsUp,
  Shield,
} from "lucide-react";
import { useLocationStore } from "@/lib/store";
import type { Location, Review } from "@shared/schema";
import { useState, useEffect, useCallback, useMemo } from "react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { RtdOptOut, RtdOptOutBanner } from "@/components/rtd-opt-out";

function RatingStars({ rating, size = "sm" }: { rating: number; size?: "sm" | "md" }) {
  const sizeClass = size === "md" ? "w-5 h-5" : "w-3.5 h-3.5";
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`${sizeClass} ${
            i < rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"
          }`}
        />
      ))}
    </div>
  );
}

function generateAiSuggestions(review: Review): string[] {
  const name = review.reviewerName.split(" ")[0];

  if (review.rating >= 4) {
    return [
      `Thank you so much for the wonderful review, ${name}! We're thrilled to hear about your positive experience. Your support means the world to our team, and we can't wait to welcome you back!`,
      `We really appreciate you taking the time to share your experience, ${name}! It's feedback like yours that keeps us motivated. We look forward to serving you again soon!`,
      `Wow, thank you ${name}! We're so glad everything exceeded your expectations. Our team works hard to deliver the best experience possible, and it's great to know it shows. See you next time!`,
    ];
  }

  if (review.rating === 3) {
    return [
      `Thank you for your honest feedback, ${name}. We're glad some aspects of your experience were positive, but we'd love to do even better next time. Would you be open to sharing more details so we can improve?`,
      `We appreciate your review, ${name}. A 3-star rating tells us there's room for improvement, and we take that seriously. Please reach out to us directly so we can make things right.`,
      `Thanks for sharing your thoughts, ${name}. We value honest feedback and are always working to improve. We'd love the chance to earn a higher rating on your next visit!`,
    ];
  }

  return [
    `We're sorry to hear about your experience, ${name}. This isn't the standard we hold ourselves to, and we'd like to make it right. Please contact us directly at your earliest convenience so we can address your concerns.`,
    `Thank you for bringing this to our attention, ${name}. We sincerely apologize for falling short of your expectations. We're reviewing what happened and would appreciate the chance to resolve this for you.`,
    `We deeply regret your negative experience, ${name}. Your feedback is important, and we're taking immediate steps to prevent this from happening again. Please reach out so we can discuss how to make this right.`,
  ];
}

function AIReplyModal({
  review,
  open,
  onOpenChange,
}: {
  review: Review;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const [replyText, setReplyText] = useState("");
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  const toneLabel = review.rating >= 4 ? "Appreciative" : review.rating === 3 ? "Constructive" : "Empathetic";
  const toneValue = review.rating >= 4 ? "appreciative" : review.rating === 3 ? "constructive" : "empathetic";
  const toneColor = review.rating >= 4 ? "text-emerald-500" : review.rating === 3 ? "text-amber-500" : "text-red-400";

  const fetchSuggestions = useCallback(async () => {
    setIsLoadingSuggestions(true);
    setSelectedIdx(null);
    setReplyText("");
    try {
      const res = await apiRequest("POST", `/api/reviews/${review.id}/ai-suggestions`, { tone: toneValue });
      const data = await res.json();
      if (data.suggestions?.length) {
        setSuggestions(data.suggestions);
      } else {
        setSuggestions(generateAiSuggestions(review));
      }
    } catch {
      setSuggestions(generateAiSuggestions(review));
      toast({ title: "AI unavailable, showing template suggestions", variant: "destructive" });
    } finally {
      setIsLoadingSuggestions(false);
    }
  }, [review, toneValue]);

  useEffect(() => {
    if (open && suggestions.length === 0) {
      fetchSuggestions();
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSuggestions([]);
      setSelectedIdx(null);
      setReplyText("");
    }
  }, [open]);

  const replyMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/gbp/reviews/${review.id}/reply`, { comment: replyText });
      return res.json();
    },
    onSuccess: (data: any) => {
      trackEvent('review_reply_sent', { review_id: review.id, rating: review.rating, char_count: replyText.length });
      queryClient.invalidateQueries({ queryKey: ["/api/reviews", review.locationId] });
      queryClient.invalidateQueries({ queryKey: ["/api/locations", review.locationId, "rtd-progress"] });
      onOpenChange(false);
      setReplyText("");
      setSelectedIdx(null);
      toast({
        title: data.synced ? "Reply synced to Google" : "Reply saved",
        description: data.synced ? undefined : "Reply saved locally but could not sync to Google at this time.",
      });
    },
  });

  const handleSelectSuggestion = (idx: number) => {
    setSelectedIdx(idx);
    setReplyText(suggestions[idx]);
  };

  const handleRegenerate = () => {
    fetchSuggestions();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-brand-orange" />
            AI Reply Assistant
          </DialogTitle>
        </DialogHeader>

        <div className="p-4 rounded-lg bg-muted/30 border border-border/50">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 shrink-0">
              <span className="text-sm font-semibold text-primary">
                {review.reviewerName.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold">{review.reviewerName}</h3>
                <RatingStars rating={review.rating} />
              </div>
              {review.text && (
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{review.text}</p>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-brand-orange" />
              <span className="text-sm font-medium">AI Suggestions</span>
              <Badge variant="secondary" className="text-xs">
                <span className={toneColor}>{toneLabel}</span> tone
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRegenerate}
              disabled={isLoadingSuggestions}
              className="text-xs"
              data-testid="button-regenerate-suggestions"
            >
              <RotateCcw className={`w-3 h-3 mr-1 ${isLoadingSuggestions ? "animate-spin" : ""}`} />
              {isLoadingSuggestions ? "Generating..." : "Regenerate"}
            </Button>
          </div>

          <div className="space-y-2">
            {isLoadingSuggestions ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="w-full p-3 rounded-lg border border-border/50 bg-muted/20 animate-pulse">
                  <div className="h-4 bg-muted/40 rounded w-full mb-2" />
                  <div className="h-4 bg-muted/40 rounded w-3/4" />
                </div>
              ))
            ) : (
              suggestions.map((suggestion, i) => (
                <button
                  key={i}
                  className={`w-full text-left p-3 rounded-lg border transition-all ${
                    selectedIdx === i
                      ? "border-brand-orange bg-brand-orange/5"
                      : "border-border/50 bg-muted/20 hover:border-border hover:bg-muted/40"
                  }`}
                  onClick={() => handleSelectSuggestion(i)}
                  data-testid={`button-suggestion-${review.id}-${i}`}
                >
                  <p className="text-sm leading-relaxed">{suggestion}</p>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Your Reply</label>
          <Textarea
            placeholder="Select a suggestion above or write your own reply..."
            value={replyText}
            onChange={(e) => {
              setReplyText(e.target.value);
              setSelectedIdx(null);
            }}
            className="min-h-[100px] resize-none"
            data-testid={`input-reply-${review.id}`}
          />
          <p className="text-xs text-muted-foreground">
            {replyText.length} characters · Recommended: 50-300 characters
          </p>
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <Button
            variant="secondary"
            onClick={() => {
              onOpenChange(false);
              setReplyText("");
              setSelectedIdx(null);
            }}
            data-testid={`button-cancel-reply-${review.id}`}
          >
            Cancel
          </Button>
          <Button
            onClick={() => replyMutation.mutate()}
            disabled={!replyText.trim() || replyMutation.isPending}
            className="bg-brand-orange hover:bg-brand-orange/90"
            data-testid={`button-send-reply-${review.id}`}
          >
            <Send className="w-4 h-4 mr-1.5" />
            {replyMutation.isPending ? "Sending..." : "Send Reply"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PendingReplyBlock({ review }: { review: Review }) {
  const { toast } = useToast();
  const [editText, setEditText] = useState(review.pendingReply || "");
  const isPending = review.replyStatus === "pending_approval" && !!review.pendingReply;

  useEffect(() => {
    setEditText(review.pendingReply || "");
  }, [review.pendingReply]);

  const approveMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/reviews/${review.id}/approve-reply`, { reply: editText });
      return res.json();
    },
    onSuccess: (data: any) => {
      trackEvent('pending_reply_approved', { review_id: review.id, rating: review.rating });
      queryClient.invalidateQueries({ queryKey: ["/api/reviews", review.locationId] });
      toast({
        title: data.synced ? "Reply approved & synced to Google" : "Reply approved",
        description: data.synced ? undefined : "Saved locally but could not sync to Google.",
      });
    },
    onError: (err: any) => {
      toast({ title: "Could not approve reply", description: err?.message, variant: "destructive" });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/reviews/${review.id}/reject-reply`);
    },
    onSuccess: () => {
      trackEvent('pending_reply_rejected', { review_id: review.id, rating: review.rating });
      queryClient.invalidateQueries({ queryKey: ["/api/reviews", review.locationId] });
      toast({ title: "Reply rejected" });
    },
  });

  if (!isPending) return null;

  return (
    <div className="mt-3 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 space-y-2" data-testid={`pending-reply-${review.id}`}>
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-amber-500" />
        <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">AI draft awaiting your approval</span>
        <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-600 dark:text-amber-400">
          {review.rating}★ — held for review
        </Badge>
      </div>
      <Textarea
        value={editText}
        onChange={(e) => setEditText(e.target.value)}
        className="min-h-[100px] text-sm resize-none bg-background"
        data-testid={`textarea-pending-reply-${review.id}`}
      />
      <p className="text-[11px] text-muted-foreground">
        Edit the draft if needed, then approve to post to Google. Negative reviews are queued so nothing posts publicly without your sign-off.
      </p>
      <div className="flex gap-2 justify-end">
        <Button
          size="sm"
          variant="outline"
          onClick={() => rejectMutation.mutate()}
          disabled={rejectMutation.isPending || approveMutation.isPending}
          data-testid={`button-reject-pending-${review.id}`}
        >
          Reject
        </Button>
        <Button
          size="sm"
          className="bg-brand-orange hover:bg-brand-orange/90"
          onClick={() => approveMutation.mutate()}
          disabled={!editText.trim() || approveMutation.isPending || rejectMutation.isPending}
          data-testid={`button-approve-pending-${review.id}`}
        >
          <Send className="w-3 h-3 mr-1" />
          {approveMutation.isPending ? "Posting..." : "Approve & Post"}
        </Button>
      </div>
    </div>
  );
}

function ReviewCard({ review }: { review: Review }) {
  const { toast } = useToast();
  const [showModal, setShowModal] = useState(false);
  const isPending = review.replyStatus === "pending_approval" && !!review.pendingReply;

  const flagMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("PATCH", `/api/reviews/${review.id}/flag`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/reviews", review.locationId] });
      toast({ title: "Review flagged for review" });
    },
  });

  const timeAgo = review.postedAt
    ? formatTimeAgo(new Date(review.postedAt))
    : "recently";

  return (
    <>
      <Card data-testid={`card-review-${review.id}`}>
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 shrink-0">
              <span className="text-sm font-semibold text-primary">
                {review.reviewerName.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <h3 className="text-sm font-semibold" data-testid={`text-reviewer-${review.id}`}>
                    {review.reviewerName}
                  </h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    <RatingStars rating={review.rating} />
                    <span className="text-xs text-muted-foreground">{timeAgo}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {isPending ? (
                    <Badge variant="outline" className="text-xs border-amber-500/40 text-amber-600 dark:text-amber-400">Pending approval</Badge>
                  ) : !review.reply ? (
                    <Badge variant="outline" className="text-xs">Needs Reply</Badge>
                  ) : null}
                  {review.isFlagged && (
                    <Badge variant="destructive" className="text-xs">Flagged</Badge>
                  )}
                </div>
              </div>

              {review.text && (
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{review.text}</p>
              )}

              {review.reply ? (
                <div className="mt-3 p-3 rounded-md bg-muted/50 border-l-2 border-brand-orange">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Your reply:</p>
                  <p className="text-sm">{review.reply}</p>
                </div>
              ) : isPending ? (
                <PendingReplyBlock review={review} />
              ) : (
                <div className="mt-3 flex gap-2 flex-wrap">
                  <Button
                    size="sm"
                    className="bg-brand-orange hover:bg-brand-orange/90"
                    onClick={() => setShowModal(true)}
                    data-testid={`button-reply-${review.id}`}
                  >
                    <Wand2 className="w-3 h-3 mr-1" />
                    AI Reply
                  </Button>
                  {!review.isFlagged && review.rating <= 2 && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => flagMutation.mutate()}
                      disabled={flagMutation.isPending}
                      data-testid={`button-flag-${review.id}`}
                    >
                      <Flag className="w-3 h-3 mr-1" />
                      Flag
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      <AIReplyModal review={review} open={showModal} onOpenChange={setShowModal} />
    </>
  );
}


function ReviewRequestLink({ placeId }: { placeId?: string | null }) {
  const [copied, setCopied] = useState(false);
  const reviewUrl = placeId
    ? `https://search.google.com/local/writereview?placeid=${placeId}`
    : null;

  const handleCopy = useCallback(() => {
    if (!reviewUrl) return;
    navigator.clipboard.writeText(reviewUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [reviewUrl]);

  if (!reviewUrl) return null;

  return (
    <div className="p-3 rounded-md bg-primary/5">
      <p className="text-xs font-medium mb-2 flex items-center gap-1">
        <Link2 className="w-3 h-3" />
        Review Request Link
      </p>
      <div className="flex gap-2">
        <a
          href={reviewUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 bg-muted rounded-md px-2.5 py-1.5 text-xs font-mono truncate hover:underline text-brand-blue"
          data-testid="link-review-request"
        >
          {reviewUrl.replace("https://", "")}
        </a>
        <Button size="sm" variant="secondary" onClick={handleCopy} data-testid="button-copy-review-link">
          {copied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
        </Button>
      </div>
    </div>
  );
}

function ReviewStats({ reviews, location }: { reviews: Review[]; location?: Location }) {
  const avgRating =
    reviews.length > 0
      ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
      : 0;
  const ratingCounts = [5, 4, 3, 2, 1].map((r) => ({
    rating: r,
    count: reviews.filter((rev) => rev.rating === r).length,
    pct: reviews.length > 0
      ? (reviews.filter((rev) => rev.rating === r).length / reviews.length) * 100
      : 0,
  }));
  const unresponded = reviews.filter((r) => !r.reply).length;
  const responded = reviews.length - unresponded;
  const responseRate = reviews.length > 0 ? Math.round((responded / reviews.length) * 100) : 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold">Review Summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="text-center">
            <p className="text-3xl font-bold" data-testid="text-avg-rating">{avgRating.toFixed(1)}</p>
            <RatingStars rating={Math.round(avgRating)} size="md" />
            <p className="text-xs text-muted-foreground mt-1">{reviews.length} reviews</p>
          </div>
          <div className="flex-1 space-y-1.5">
            {ratingCounts.map((rc) => (
              <div key={rc.rating} className="flex items-center gap-2">
                <span className="text-xs w-3 text-right">{rc.rating}</span>
                <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                <div className="flex-1 h-2 bg-muted rounded-full">
                  <div
                    className="h-full bg-amber-400 rounded-full transition-all"
                    style={{ width: `${rc.pct}%` }}
                  />
                </div>
                <span className="text-xs text-muted-foreground w-8">{rc.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div className="p-3 rounded-md bg-muted/30 text-center">
            <p className="text-lg font-bold text-amber-500" data-testid="text-unresponded">{unresponded}</p>
            <p className="text-xs text-muted-foreground">Need Reply</p>
          </div>
          <div className="p-3 rounded-md bg-muted/30 text-center">
            <p className="text-lg font-bold text-emerald-500" data-testid="text-responded">
              {responded}
            </p>
            <p className="text-xs text-muted-foreground">Responded</p>
          </div>
          <div className="p-3 rounded-md bg-muted/30 text-center">
            <p className="text-lg font-bold text-brand-blue" data-testid="text-response-rate">
              {responseRate}%
            </p>
            <p className="text-xs text-muted-foreground">Rate</p>
          </div>
        </div>

        <ReviewRequestLink placeId={location?.gbpPlaceId} />
      </CardContent>
    </Card>
  );
}

export default function ReviewsPage() {
  const { selectedLocationId } = useLocationStore();
  const [filter, setFilter] = useState("all");

  const { data: locations } = useQuery<Location[]>({
    queryKey: ["/api/locations"],
  });

  const locationId = selectedLocationId || locations?.[0]?.id;

  const { data: reviews = [], isLoading } = useQuery<Review[]>({
    queryKey: ["/api/reviews", locationId],
    enabled: !!locationId,
  });

  const filteredReviews = (() => {
    switch (filter) {
      case "unresponded":
        return reviews.filter((r) => !r.reply);
      case "positive":
        return reviews.filter((r) => r.rating >= 4);
      case "negative":
        return reviews.filter((r) => r.rating <= 3);
      case "flagged":
        return reviews.filter((r) => r.isFlagged);
      default:
        return reviews;
    }
  })();

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Skeleton className="h-[300px]" />
          <Skeleton className="h-[300px] lg:col-span-2" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24 space-y-6 overflow-y-auto h-full">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="text-reviews-title">
            Reviews & Reputation
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Monitor, respond, and manage your online reviews
          </p>
        </div>
        <RtdOptOut
          locationId={locationId}
          taskIds={["reviews"]}
          stageName="Reviews"
          warningTitle="Skip review management for now?"
          warningBody={
            <p>
              Heads up — reviews are one of the strongest local ranking factors. A steady flow of fresh,
              responded-to reviews tells Google your business is active and trustworthy, and it directly
              influences how often you show up in the Map Pack. Skipping this step usually means lower
              conversion and slower ranking growth.
            </p>
          }
        />
      </div>
      <RtdOptOutBanner
        locationId={locationId}
        taskIds={["reviews"]}
        stageName="Reviews"
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-1">
          <ReviewStats reviews={reviews} location={locations?.find(l => l.id === locationId)} />
        </div>

        <div className="lg:col-span-3 space-y-4">
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList>
              <TabsTrigger value="all" data-testid="tab-all-reviews">All ({reviews.length})</TabsTrigger>
              <TabsTrigger value="unresponded" data-testid="tab-unresponded">
                Needs Reply ({reviews.filter((r) => !r.reply).length})
              </TabsTrigger>
              <TabsTrigger value="positive" data-testid="tab-positive">
                Positive ({reviews.filter((r) => r.rating >= 4).length})
              </TabsTrigger>
              <TabsTrigger value="negative" data-testid="tab-negative">
                Negative ({reviews.filter((r) => r.rating <= 3).length})
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="space-y-3">
            {filteredReviews.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center py-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <Star className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold">No Reviews Found</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    {filter === "all"
                      ? "Reviews will appear here when customers leave feedback."
                      : "No reviews match this filter."}
                  </p>
                </CardContent>
              </Card>
            ) : (
              filteredReviews.map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
